import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    RiSaveLine,
    RiLoader4Line
} from 'react-icons/ri';
import { API_BASE_URL } from '../../config';

const ManageAboutContent = () => {
    const [contents, setContents] = useState({
        about_hero: { title: '', description: '' },
        about_values: { title: '', subtitle: '' },
        about_quote: { text: '', author: '' }
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const fetchAllContent = useCallback(async () => {
        setLoading(true);
        try {
            const keys = ['about_hero', 'about_values', 'about_quote'];
            const newContents = { ...contents };
            for (const key of keys) {
                const res = await fetch(`${API_BASE_URL}/content/read.php?key=${key}`);
                const data = await res.json();
                if (res.ok && data.content) {
                    newContents[key] = data.content;
                }
            }
            setContents(newContents);
        } catch (error) {
            console.error('Error fetching content:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllContent();
    }, [fetchAllContent]);

    const handleSave = async (key, content) => {
        setSaving(key);
        try {
            const response = await fetch(`${API_BASE_URL}/content/update.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ key, content })
            });

            if (response.ok) {
                // Success feedback could go here
            }
        } catch (error) {
            console.error('Error saving content:', error);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-32 gap-4">
            <RiLoader4Line className="animate-spin text-emerald-500 text-5xl" />
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">Accessing Brand Matrix...</span>
        </div>
    );

    return (
        <div className="max-w-5xl">
            <header className="mb-16">
                <div className="flex items-center gap-3 text-emerald-600 mb-3">
                    <div className="flex gap-1">
                        <div className="w-3 h-1 bg-emerald-500 rounded-full" />
                        <div className="w-1 h-1 bg-emerald-500/30 rounded-full" />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.4em] font-sans">Narrative Synchronization protocol</span>
                </div>
                <h2 className="text-5xl font-bold text-slate-900 font-display tracking-tight">Public Presence</h2>
            </header>

            <div className="space-y-12">
                {/* Hero Section Card */}
                <ContentCard
                    title="Hero Narrative"
                    subtitle="Primary brand positioning and introduction"
                    onSave={() => handleSave('about_hero', contents.about_hero)}
                    saving={saving === 'about_hero'}
                >
                    <div className="grid gap-8">
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Main Heading</label>
                            <input
                                type="text"
                                value={contents.about_hero.title}
                                onChange={(e) => setContents({
                                    ...contents,
                                    about_hero: { ...contents.about_hero, title: e.target.value }
                                })}
                                className="w-full px-6 py-5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/5 transition-all text-xl font-bold text-slate-900"
                            />
                        </div>
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Mission Statement</label>
                            <textarea
                                value={contents.about_hero.description}
                                onChange={(e) => setContents({
                                    ...contents,
                                    about_hero: { ...contents.about_hero, description: e.target.value }
                                })}
                                className="w-full px-6 py-5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/5 transition-all text-sm font-medium text-slate-600 min-h-[120px] leading-relaxed"
                            />
                        </div>
                    </div>
                </ContentCard>

                {/* Values Section Card */}
                <ContentCard
                    title="Core Values HUD"
                    subtitle="Grid section headers and sub-text"
                    onSave={() => handleSave('about_values', contents.about_values)}
                    saving={saving === 'about_values'}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Section Title</label>
                            <input
                                type="text"
                                value={contents.about_values.title}
                                onChange={(e) => setContents({
                                    ...contents,
                                    about_values: { ...contents.about_values, title: e.target.value }
                                })}
                                className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500/50 transition-all font-bold text-slate-900"
                            />
                        </div>
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Editorial Subtitle</label>
                            <input
                                type="text"
                                value={contents.about_values.subtitle}
                                onChange={(e) => setContents({
                                    ...contents,
                                    about_values: { ...contents.about_values, subtitle: e.target.value }
                                })}
                                className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500/50 transition-all font-bold text-slate-900"
                            />
                        </div>
                    </div>
                </ContentCard>

                {/* Leadership Quote Card */}
                <ContentCard
                    title="Executive Quote"
                    subtitle="Featured bottom narrative and attribution"
                    onSave={() => handleSave('about_quote', contents.about_quote)}
                    saving={saving === 'about_quote'}
                >
                    <div className="grid gap-8">
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Quote Body</label>
                            <textarea
                                value={contents.about_quote.text}
                                onChange={(e) => setContents({
                                    ...contents,
                                    about_quote: { ...contents.about_quote, text: e.target.value }
                                })}
                                className="w-full px-6 py-5 bg-slate-50 border-2 border-transparent rounded-3xl focus:bg-white focus:border-emerald-500/50 transition-all text-lg font-medium text-slate-700 italic min-h-[100px]"
                            />
                        </div>
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Author Attribution</label>
                            <input
                                type="text"
                                value={contents.about_quote.author}
                                onChange={(e) => setContents({
                                    ...contents,
                                    about_quote: { ...contents.about_quote, author: e.target.value }
                                })}
                                className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500/50 transition-all font-black text-emerald-600 uppercase tracking-widest text-xs"
                                placeholder="Name, Title"
                            />
                        </div>
                    </div>
                </ContentCard>
            </div>
        </div>
    );
};

const ContentCard = ({ title, subtitle, children, onSave, saving }) => (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden group hover:shadow-xl hover:shadow-emerald-950/5 transition-all">
        <div className="p-10 border-b border-slate-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-slate-50/30">
            <div>
                <h3 className="text-2xl font-bold text-slate-900 font-display mb-1">{title}</h3>
                <p className="text-xs text-slate-400 font-medium tracking-tight font-sans">{subtitle}</p>
            </div>
            <button
                onClick={onSave}
                disabled={saving}
                className="flex items-center gap-3 bg-slate-900 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-emerald-600 disabled:bg-slate-200 transition-all shadow-xl shadow-slate-900/10 active:scale-95"
            >
                {saving ? (
                    <>
                        <RiLoader4Line className="animate-spin" size={18} />
                        Syncing...
                    </>
                ) : (
                    <>
                        <RiSaveLine size={18} />
                        Update Database
                    </>
                )}
            </button>
        </div>
        <div className="p-10 bg-white relative">
            {children}
            {/* Corner Decorative Element */}
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-[radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.02),transparent)] pointer-events-none" />
        </div>
    </div>
);

export default ManageAboutContent;
